/**
 * 页面组件
 * 1. 支持children模式和tabs模式
 * 2. tabs模式可以自动生成路由和链接
 */
import React from 'react';
import {Navigate, NavLink, useRoutes} from 'react-router-dom';
import classNames from 'classnames';
import styles from './index.module.scss';
import useAuth from '../../../../../Utils/Hooks/useAuth';
import Boundary from '../../../../Boundary';
import {Spin} from 'antd';

interface TabItem {
    name: string;
    path: string;
    auth?: string;
    element: React.ReactElement;
}

interface PagePropsBase {
    className?: string;
    title?: React.ReactNode;
    bodyNoStyle?: boolean;
    bodyClassName?: string;
}

interface TabPage extends PagePropsBase {
    tabs: TabItem[];
    children?: undefined;
}

interface ChildPage extends PagePropsBase {
    children: React.ReactNode;
}

type PageProps = TabPage | ChildPage;

export default function Page({
    className,
    title,
    bodyNoStyle = false,
    bodyClassName,
    ...restProps
}: PageProps) {
    const auth = useAuth();
    const tabs = 'tabs' in restProps ? restProps.tabs : [];

    const authedPages = tabs.filter(page => !page.auth || auth(page.auth));

    const routes = useRoutes([
        ...authedPages,
        {
            path: '*',
            element: (
                <Navigate
                    replace
                    to={authedPages.length === 0 ? '/' : authedPages[0]?.path}
                />
            ),
        },
    ]);

    function renderChildren() {
        if ('tabs' in restProps) return routes;
        return 'children' in restProps && restProps.children;
    }

    return (
        <div className={classNames(className, styles.root)}>
            {(title || tabs.length !== 0) && (
                <div className={classNames(styles.header)}>
                    {title && <h1 className={styles.title}>{title}</h1>}

                    <div className={styles.tabs}>
                        {authedPages.map(item => (
                            <NavLink
                                key={item.path}
                                className={({isActive}) =>
                                    classNames(styles.tab, {
                                        [styles.current]: isActive,
                                    })
                                }
                                to={item.path}>
                                {item.name}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}

            <div
                className={classNames(styles.body, {
                    [styles.bodyNormal]: !bodyNoStyle,
                })}>
                <Boundary spinner={<Spin />}>{renderChildren()}</Boundary>
            </div>
        </div>
    );
}
